import React from "react";

/**
 * Shared thin-stroke icon set. Every path is drawn on a 24x24 grid and
 * inherits currentColor, so an icon takes its colour from its container.
 *
 * Pass `filled` for the few glyphs that are solid shapes (brand marks, quote).
 */
const ICON_PATHS = {
  /* --- Service categories ------------------------------------------------ */
  restaurants: (
    <>
      <path d="M7 3v7a2.5 2.5 0 0 0 5 0V3" />
      <path d="M9.5 12.5V21" />
      <path d="M17.8 3c-1.4 1.7-2.1 3.5-2.1 5.4 0 1.7.7 2.7 2.1 3V21" />
    </>
  ),
  fashion: (
    <>
      <path d="M8.5 3 4 5.2l1.8 4.2 2-.9V21h8.4V8.5l2 .9L20 5.2 15.5 3" />
      <path d="M8.5 3a3.5 3.5 0 0 0 7 0" />
    </>
  ),
  gifts: (
    <>
      <path d="M20 12v8.5a.5.5 0 0 1-.5.5h-15a.5.5 0 0 1-.5-.5V12" />
      <path d="M2.5 7.5h19V12h-19z" />
      <path d="M12 7.5V21" />
      <path d="M12 7.5H7.75a2.625 2.625 0 1 1 0-5.25C11 2.25 12 7.5 12 7.5Z" />
      <path d="M12 7.5h4.25a2.625 2.625 0 1 0 0-5.25C13 2.25 12 7.5 12 7.5Z" />
    </>
  ),
  electronics: (
    <>
      <path d="M3 5h18v11.5H3z" />
      <path d="M12 16.5V21" />
      <path d="M8 21h8" />
    </>
  ),
  home: (
    <>
      <path d="M3 10.6 12 3.4l9 7.2" />
      <path d="M5.2 12.4V21h13.6v-8.6" />
      <path d="M9.6 21v-5.4h4.8V21" />
    </>
  ),
  cars: (
    <>
      <path d="M4.5 16.5v-4l2-5.5h11l2 5.5v4" />
      <path d="M3.5 16.5h17" />
      <path d="M5.5 16.5V19h3v-2.5" />
      <path d="M15.5 16.5V19h3v-2.5" />
      <path d="M7.5 12.6h.01M16.5 12.6h.01" />
    </>
  ),
  realEstate: (
    <>
      <path d="M4.5 21V7.2L12 3l7.5 4.2V21" />
      <path d="M3 21h18" />
      <path d="M9.5 21v-5h5v5" />
      <path d="M8.5 9.5h.01M12 9.5h.01M15.5 9.5h.01M8.5 13h.01M15.5 13h.01" />
    </>
  ),
  healthcare: (
    <>
      <path d="M12 20.5C9.6 18.9 3.5 14.8 3.5 10.2A4.7 4.7 0 0 1 12 7.4a4.7 4.7 0 0 1 8.5 2.8c0 4.6-6.1 8.7-8.5 10.3Z" />
      <path d="M3.9 12.6h4L9.4 10l2 5 1.6-3h5.2" />
    </>
  ),
  tourism: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.2 12h17.6" />
      <path d="M12 3c2.4 2.5 3.8 5.7 3.9 9-.1 3.3-1.5 6.5-3.9 9-2.4-2.5-3.8-5.7-3.9-9C8.2 8.7 9.6 5.5 12 3Z" />
    </>
  ),
  factories: (
    <>
      <path d="M3 21h18" />
      <path d="M4.5 21V10.2l5 3V10.2l5 3V5.6l5 3V21" />
      <path d="M8 17.2h.01M13 17.2h.01M17.5 17.2h.01" />
    </>
  ),
  market: (
    <>
      <path d="M3 4.5h2.2l2.3 10.7a1.5 1.5 0 0 0 1.5 1.2h8.1a1.5 1.5 0 0 0 1.5-1.2L20.6 8H6" />
      <circle cx="10" cy="20" r="1.1" />
      <circle cx="17" cy="20" r="1.1" />
    </>
  ),
  cinema: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <path d="M7.5 4.5v15M16.5 4.5v15" />
      <path d="M3 9.5h4.5M3 14.5h4.5M16.5 9.5H21M16.5 14.5H21" />
    </>
  ),
  bazaar: (
    <>
      <path d="M3.9 13.1 11 6h7.6v7.6l-7.1 7.1a1.5 1.5 0 0 1-2.1 0l-5.5-5.5a1.5 1.5 0 0 1 0-2.1Z" />
      <circle cx="15.2" cy="9.4" r="1.2" />
    </>
  ),

  /* --- Interface --------------------------------------------------------- */
  check: <path d="m4.5 12.5 5 5 10-11" />,
  arrow: <path d="M4.5 12h15m0 0-6-6m6 6-6 6" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6 6 18" />,
  grid: (
    <>
      <path d="M4 4h6.5v6.5H4zM13.5 4H20v6.5h-6.5zM4 13.5h6.5V20H4zM13.5 13.5H20V20h-6.5z" />
    </>
  ),
  list: (
    <>
      <path d="M4 6.5h16M4 12h16M4 17.5h16" />
    </>
  ),
  sliders: (
    <>
      <path d="M4 7.5h10M18 7.5h2M4 16.5h4M12 16.5h8" />
      <circle cx="16" cy="7.5" r="2" />
      <circle cx="10" cy="16.5" r="2" />
    </>
  ),
  reset: (
    <>
      <path d="M20 5.5v5h-5" />
      <path d="M19.4 10.5A7.6 7.6 0 1 0 20 14.2" />
    </>
  ),
  sparkle: (
    <>
      <path d="M12 3.5 13.9 9l5.6 2-5.6 2-1.9 5.5L10.1 13l-5.6-2 5.6-2z" />
      <path d="M18.5 15.5v4M16.5 17.5h4" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 4.8 6v6c0 4.1 2.9 7.7 7.2 9 4.3-1.3 7.2-4.9 7.2-9V6z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </>
  ),
  bolt: <path d="M13.2 3 5 13.6h5.6L9.9 21l8.4-10.7h-5.7z" />,
  quote: (
    <path d="M9.4 5.5C6.3 7 4.5 9.8 4.5 13v5.5h6.6V12H8.3c0-2 .9-3.6 2.7-4.6Zm9.3 0C15.6 7 13.8 9.8 13.8 13v5.5h6.6V12h-2.8c0-2 .9-3.6 2.7-4.6Z" />
  ),

  /* --- Contact ----------------------------------------------------------- */
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m4.2 7.2 7.1 5a1.2 1.2 0 0 0 1.4 0l7.1-5" />
    </>
  ),
  phone: (
    <path d="M6.4 3.5h3.1l1.5 4-2 1.4a12.2 12.2 0 0 0 6.1 6.1l1.4-2 4 1.5v3.1a1.9 1.9 0 0 1-2.1 1.9A16.6 16.6 0 0 1 4.5 5.6a1.9 1.9 0 0 1 1.9-2.1Z" />
  ),
  chat: (
    <path d="M20.5 11.6a8.4 8.4 0 0 1-12.6 7.3l-4.4 1.2 1.2-4.3A8.4 8.4 0 1 1 20.5 11.6Z" />
  ),
  pin: (
    <>
      <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.6" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.2l3.4 2" />
    </>
  ),
  map: (
    <>
      <path d="M9 4.5 3.5 7v12.5L9 17l6 2.5 5.5-2.5V4.5L15 7 9 4.5Z" />
      <path d="M9 4.5V17M15 7v12.5" />
    </>
  ),
  send: (
    <>
      <path d="M20.5 3.5 3.8 11.2l6.6 2.4 2.4 6.6L20.5 3.5Z" />
      <path d="M10.4 13.6 20.5 3.5" />
    </>
  ),
  alert: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.8v4.7M12 16.1h.01" />
    </>
  ),
  checkCircle: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.2 12.2 2.6 2.6 5-5.4" />
    </>
  ),
  chevron: <path d="m7 10 5 5 5-5" />,
  arrowLeft: <path d="M19.5 12h-15m0 0 6-6m-6 6 6 6" />,
  smartphone: (
    <>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.8" />
      <path d="M10.4 18.6h3.2" />
    </>
  ),
  external: (
    <>
      <path d="M14 4.5h5.5V10M19.5 4.5 11.2 12.8" />
      <path d="M18 13.6v4.9a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6h4.9" />
    </>
  ),

  /* --- Values ------------------------------------------------------------ */
  rocket: (
    <>
      <path d="M14.6 3.2c2.7 0 6.2 3.5 6.2 6.2-1.9 4.5-5.2 7.8-9.7 9.7l-2.8-2.8-2.8-2.8c1.9-4.5 5.2-7.8 9.1-10.3Z" />
      <circle cx="14.4" cy="9.6" r="1.8" />
      <path d="M7.8 16.2 4.4 19.6M6 13.1 4 14a4.8 4.8 0 0 0-1 3M10.9 18l-.9 2a4.8 4.8 0 0 0 3-1" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path d="M15.5 5.2a3.2 3.2 0 0 1 0 5.9M17 14.6a5.5 5.5 0 0 1 3.5 4.9" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.8" />
      <circle cx="12" cy="12" r="1.3" />
    </>
  ),
  bulb: (
    <>
      <path d="M12 3a6 6 0 0 0-3.6 10.8c.6.5 1 1.2 1 1.9v.8h5.2v-.8c0-.7.4-1.4 1-1.9A6 6 0 0 0 12 3Z" />
      <path d="M9.9 19h4.2M10.6 21.2h2.8" />
    </>
  ),

  /* --- Social ------------------------------------------------------------ */
  facebook: (
    <path d="M13.4 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5h1.65V3.6c-.3-.04-1.27-.13-2.4-.13-2.4 0-4.02 1.45-4.02 4.12v2.3H7.4V13h2.78v8h3.22Z" />
  ),
  instagram: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <path d="M16.8 7.2h.01" />
    </>
  ),
  x: (
    <path d="M3.4 3h5.2l4.1 5.6L17.8 3h2.7l-6.3 7.2L21 21h-5.2l-4.4-6-5.3 6H3.4l6.8-7.8L3.4 3Z" />
  ),
  linkedin: (
    <path d="M5 3.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM3 21h4V9.6H3V21Zm7 0h4v-6.2c0-1.6.3-3.2 2.3-3.2s2 1.8 2 3.3V21h4v-7c0-3.5-.8-6-4.8-6-1.9 0-3.2 1.05-3.7 2.05h-.06V9.6H10V21Z" />
  ),
  youtube: (
    <>
      <rect x="2.5" y="5.5" width="19" height="13" rx="4.2" />
      <path d="M10.3 9.3 15.6 12l-5.3 2.7z" />
    </>
  ),

  /* --- App stores -------------------------------------------------------- */
  apple: (
    <path d="M15.9 12.4c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.2-2.8.8-3.5.8-.7 0-1.8-.8-3-.8-1.5 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 3 2.3 1.2 0 1.6-.8 3.1-.8s1.9.8 3.1.7c1.3 0 2.1-1.1 2.9-2.3.9-1.3 1.3-2.6 1.3-2.7-.1 0-2.5-1-2.9-3.5ZM13.6 5.6c.6-.8 1.1-1.9 1-3-.9 0-2.1.6-2.8 1.4-.6.7-1.2 1.9-1 3 1 .1 2.1-.5 2.8-1.4Z" />
  ),
  play: (
    <path d="M4.5 3.2 15 12 4.5 20.8a1 1 0 0 1-.5-.9V4.1a1 1 0 0 1 .5-.9ZM15 12l3.9-3.3 2.4 1.4a2.2 2.2 0 0 1 0 3.8l-2.4 1.4Z" />
  ),
  store: (
    <>
      <path d="M4 9.5V20a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5" />
      <path d="M3 9.5 5 3.5h14l2 6a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0Z" />
    </>
  )
};

function Icon({ name, className = "w-6 h-6", filled = false }) {
  const glyph = ICON_PATHS[name];
  if (!glyph) return null;

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke={filled ? "none" : "currentColor"}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {glyph}
    </svg>
  );
}

export default Icon;
export { ICON_PATHS };
